import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X, ShoppingBag } from "lucide-react";
import { LOGO } from "../lib/menu";
import { useOrder } from "../context/OrderContext";

const NAV = [
  { to: "/", label: "Home", testid: "drawer-nav-home" },
  { to: "/menu-samenstellen", label: "Menu", testid: "drawer-nav-menu" },
  { to: "/verhalen", label: "Verhalen", testid: "drawer-nav-verhalen" },
  { to: "/contact", label: "Contact", testid: "drawer-nav-contact" },
];

export const Header = () => {
  const { pathname } = useLocation();
  const { order } = useOrder();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const cartCount = Object.values(order?.quantities || {}).reduce((a, b) => a + (Number(b) || 0), 0);

  // Close the drawer whenever the route changes and lock body scroll while open.
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const isActive = (to) => (to === "/" ? pathname === "/" : pathname.startsWith(to));

  return (
    <>
      <header
        className="fixed top-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-b-2 border-[#E8F5E9] shadow-[0_2px_12px_-6px_rgba(13,59,76,0.35)]"
        data-testid="site-header"
      >
        <div className="max-w-[1200px] mx-auto w-full px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-home-link">
            <img src={LOGO} alt="De Zeeuwse Tori" className="w-12 h-12 rounded-full object-cover" />
            <span className="font-[700] text-[#0D3B4C] text-lg hidden sm:inline">De Zeeuwse Tori</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/menu-samenstellen"
              aria-label={`Winkelmandje, ${cartCount} porties`}
              data-testid="header-cart"
              className="relative flex items-center justify-center w-12 h-12 border-2 border-[#0D3B4C] text-[#0D3B4C] hover:bg-[#0D3B4C] hover:text-white transition-colors"
            >
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span
                  data-testid="header-cart-count"
                  className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1 flex items-center justify-center rounded-full bg-[#B71C1C] text-white text-xs font-[700] border-2 border-white"
                >
                  {cartCount}
                </span>
              )}
            </Link>
            <button
              onClick={() => setOpen(true)}
              aria-label="Menu openen"
              aria-expanded={open}
              data-testid="menu-burger-button"
              className="flex items-center gap-2 border-2 border-[#1B6B2E] text-[#1B6B2E] px-4 py-2.5 hover:bg-[#1B6B2E] hover:text-white transition-colors font-[700] uppercase tracking-wider text-sm"
            >
              <MenuIcon size={20} />
              <span className="hidden sm:inline">Menu</span>
            </button>
          </div>
        </div>
      </header>

      {/* Side drawer */}
      <div className={`fixed inset-0 z-[60] ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
        <div
          onClick={close}
          data-testid="menu-drawer-overlay"
          className={`absolute inset-0 bg-[#0D3B4C]/60 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
        />
        <aside
          role="dialog"
          aria-modal="true"
          aria-label="Navigatiemenu"
          data-testid="menu-drawer"
          className={`absolute right-0 top-0 h-full w-[320px] max-w-[84%] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
        >
          {/* Drawer header with small logo */}
          <div className="flex items-center justify-between px-6 h-24 bg-[#FEF9E7] border-b-2 border-[#E8F5E9]">
            <Link to="/" onClick={close} className="flex items-center gap-3" data-testid="drawer-logo-link">
              <img src={LOGO} alt="De Zeeuwse Tori" className="w-12 h-12 rounded-full object-cover" />
              <span className="font-[700] text-[#0D3B4C] leading-tight">De Zeeuwse<br />Tori</span>
            </Link>
            <button
              onClick={close}
              aria-label="Menu sluiten"
              data-testid="menu-drawer-close"
              className="w-10 h-10 flex items-center justify-center text-[#0D3B4C] hover:text-[#B71C1C] transition-colors"
            >
              <X size={26} />
            </button>
          </div>

          {/* Nav links */}
          <nav className="flex-1 flex flex-col py-4">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={close}
                data-testid={item.testid}
                className={`group relative px-6 py-4 font-[700] text-xl tracking-tight transition-colors ${
                  isActive(item.to) ? "text-[#1B6B2E]" : "text-[#0D3B4C] hover:text-[#1B6B2E]"
                }`}
              >
                <span
                  className={`absolute left-0 top-1/2 -translate-y-1/2 w-1.5 rounded-r bg-[#F4D03F] transition-all duration-200 ${
                    isActive(item.to) ? "h-8" : "h-0 group-hover:h-8"
                  }`}
                />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Brand accent */}
          <div className="px-6 pb-6">
            <p className="text-sm font-[600] text-[#0D3B4C] mb-3">Authentiek · Home-made · Surinaamse keuken</p>
            <div className="flex h-2 overflow-hidden rounded-full">
              <span className="flex-1 bg-[#1B6B2E]" />
              <span className="flex-1 bg-[#B71C1C]" />
              <span className="flex-1 bg-[#F4D03F]" />
            </div>
          </div>
        </aside>
      </div>
    </>
  );
};
